import {
  createMemo,
  createResource,
  createSignal,
  For,
  Show,
  type JSX,
} from "solid-js";
import { useSearchParams } from "@solidjs/router";
import type {
  InvoiceDraft,
  BillingPoint,
  BudgetInput,
  BudgetStatus,
  CostBreakdown,
  InvoiceCheck,
  MonthCost,
  TariffInput,
  TariffPrices,
  TariffVersion,
  ZoneSchedule,
} from "@energyplatform/client";
import { api } from "../services.ts";
import { locale, t } from "../i18n.ts";
import { formatDateTime, formatNumber, formatShare } from "../format.ts";
import { depthFirst, pathTo, range, scope, siteTree } from "../context.ts";
import { Icon } from "../icons.tsx";
import { MediumBadge } from "../components/Media.tsx";
import {
  type BilledMedium,
  billedMedium,
  CATEGORIES_OF,
  invoiceRows,
  nextZone,
  PRICE_FIELDS_OF,
  spreadBudget,
  zoneRuns,
} from "../finance.ts";
import { Chart, type ChartSeries } from "../components/Chart.tsx";
import { ErrorBox } from "../components/ErrorBox.tsx";
import {
  ConfirmDialog,
  Dialog,
  operator,
  PageHeader,
  Skeleton,
  Tabs,
  useAction,
} from "../components/ui.tsx";

const TABS = ["costs", "terms", "budgets", "invoices"] as const;
type Tab = (typeof TABS)[number];
const CATEGORIES = [
  "energy",
  "distribution",
  "statutory",
  "reactive",
  "water",
  "sewage",
  "other",
] as const;
const ZONE_COLOR: Record<string, string> = {
  all: "var(--accent)",
  peak: "var(--crit)",
  offpeak: "var(--ok)",
  s1: "var(--warn)",
  s2: "var(--crit)",
  s3: "var(--ok)",
};

const zl = (v: number | null | undefined, digits = 0) =>
  v === null || v === undefined
    ? "—"
    : `${formatNumber(v, locale(), digits)} zł`;
const today = () => new Date().toISOString().slice(0, 10);

/**
 * Finance (prototype "Finanse"): costs of the scope estimated from metered energy and the terms entered for billing
 * points, the terms themselves with versions from a date, budgets and invoices reconciled against the estimate.
 */
export function FinancePage() {
  const [params, setParams] = useSearchParams<{ tab?: string }>();
  const tab = (): Tab =>
    TABS.includes(params.tab as Tab) ? (params.tab as Tab) : "costs";
  const [points, { refetch: refetchPoints }] = createResource(() =>
    api.finance.billingPoints(),
  );
  const [versions, { refetch: refetchVersions }] = createResource(() =>
    api.finance.versions(),
  );
  const [groups] = createResource(() => api.finance.tariffGroups());
  const [invoices, { refetch: refetchInvoices }] = createResource(() =>
    api.finance.invoices(),
  );
  const reload = () => {
    api.invalidate("/api/v1/finance");
    void refetchPoints();
    void refetchVersions();
    void refetchInvoices();
  };
  const toExplain = () =>
    (invoices() ?? []).filter((i) => i.invoice.status === "toExplain").length;

  return (
    <>
      <PageHeader
        eyebrow={`${t("finance.eyebrow")} · ${scope()?.name ?? ""}`}
        title={t(`finance.title.${tab()}`)}
        sub={t(`finance.intro.${tab()}`)}
      />
      <Tabs
        tabs={TABS}
        value={tab()}
        onChange={(x) => setParams({ tab: x })}
        label={(x) =>
          x === "invoices" && toExplain() > 0
            ? t("finance.tab.invoicesToExplain", { count: toExplain() })
            : t(`finance.tab.${x}`)
        }
      />
      <Show when={tab() === "costs"}>
        <Show when={scope()} keyed>
          {(node) => (
            <Costs
              nodeId={node.id}
              hasPoints={(points() ?? []).length > 0}
              onTerms={() => setParams({ tab: "terms" })}
            />
          )}
        </Show>
      </Show>
      <Show when={tab() === "terms"}>
        <Terms
          points={points() ?? []}
          versions={versions() ?? []}
          groups={groups() ?? []}
          loading={points.loading}
          onChanged={reload}
        />
      </Show>
      <Show when={tab() === "budgets"}>
        <Budgets />
      </Show>
      <Show when={tab() === "invoices"}>
        <Invoices
          points={points() ?? []}
          invoices={invoices() ?? []}
          loading={invoices.loading}
          onChanged={reload}
        />
      </Show>
    </>
  );
}

function Kpi(props: { label: string; value: string; note?: string }) {
  return (
    <section class="card kpi">
      <span class="lbl">{props.label}</span>
      <span class="val">{props.value}</span>
      <Show when={props.note}>
        <span class="q">{props.note}</span>
      </Show>
    </section>
  );
}

function Costs(props: {
  nodeId: string;
  hasPoints: boolean;
  onTerms: () => void;
}) {
  const [costs] = createResource(
    () => ({ id: props.nodeId, ...range() }),
    (q) => api.finance.costs(q.id, q.from, q.to),
  );
  const year = new Date().getFullYear();
  const [monthly] = createResource(
    () => props.nodeId,
    (id) => api.finance.monthly(id, year),
  );
  const [selected, setSelected] = createSignal<string>();
  // Energy and its price are electricity's: gas kWh and water m³ would distort them.
  const electricity = () =>
    (costs()?.points ?? []).filter((p) => p.medium === "el");
  const energy = () =>
    electricity().reduce((a, p) => a + p.estimate.energyKwh, 0);
  const electricityCost = () =>
    electricity().reduce((a, p) => a + p.estimate.total, 0);
  const completeness = () => {
    const ps = costs()?.points ?? [];
    return ps.length === 0
      ? null
      : ps.reduce((a, p) => a + p.estimate.completeness, 0) / ps.length;
  };
  const detail = () =>
    (costs()?.points ?? []).find(
      (p) =>
        p.billingPoint.id ===
        (selected() ?? costs()?.points[0]?.billingPoint.id),
    );
  const chart = createMemo<ChartSeries[]>(() => {
    const m = monthly() ?? [];
    const at = (month: number) => new Date(year, month - 1, 1).getTime();
    const out: ChartSeries[] = [
      {
        key: "cost",
        label: t("finance.costs.cost"),
        color: "var(--accent)",
        kind: "bar",
        points: m.map((x) => ({ t: at(x.month), v: x.cost })),
      },
    ];
    if (m.some((x) => x.plan !== null)) {
      out.push({
        key: "plan",
        label: t("finance.costs.plan"),
        color: "var(--muted)",
        kind: "line",
        dashed: true,
        points: m.map((x) => ({ t: at(x.month) + 15 * 86_400_000, v: x.plan })),
      });
    }
    return out;
  });

  return (
    <Show
      when={props.hasPoints}
      fallback={
        <section class="card empty">
          <h2>{t("finance.costs.noTermsTitle")}</h2>
          <p class="muted">{t("finance.costs.noTerms")}</p>
          <div>
            <button type="button" class="btn primary" onClick={props.onTerms}>
              <Icon name="plus" />
              {t("finance.terms.addPoint")}
            </button>
          </div>
        </section>
      }
    >
      <Show when={!costs.error} fallback={<ErrorBox error={costs.error} />}>
        <Show
          when={costs()}
          fallback={
            <section class="card">
              <Skeleton lines={5} />
            </section>
          }
        >
          {(c) => (
            <>
              <div class="grid g-kpi">
                <Kpi
                  label={t("finance.costs.total")}
                  value={zl(c().total)}
                  note={t("finance.costs.estimateNote")}
                />
                <Show when={electricity().length > 0}>
                  <Kpi
                    label={t("finance.costs.energy")}
                    value={`${formatNumber(energy(), locale(), 0)} kWh`}
                  />
                  <Kpi
                    label={t("finance.costs.unitPrice")}
                    value={
                      energy() > 0
                        ? `${formatNumber(electricityCost() / energy(), locale(), 3)} ${t("finance.unit.perKwh")}`
                        : "—"
                    }
                    note={t("finance.costs.unitPriceNote")}
                  />
                </Show>
                <Kpi
                  label={t("finance.costs.completeness")}
                  value={formatShare(completeness(), locale())}
                  note={t("finance.costs.completenessNote")}
                />
              </div>
              <div class="grid g-2">
                <section class="card">
                  <div class="card-h">
                    <h2>{t("finance.costs.byCategory")}</h2>
                    <span class="meta">zł netto</span>
                  </div>
                  <table class="tbl">
                    <tbody>
                      <For
                        each={CATEGORIES.filter(
                          (k) => (c().byCategory[k] ?? 0) !== 0,
                        )}
                      >
                        {(k) => (
                          <tr>
                            <td>{t(`finance.category.${k}`)}</td>
                            <td class="r num">{zl(c().byCategory[k], 2)}</td>
                            <td class="r hint">
                              {formatShare(
                                c().total > 0
                                  ? c().byCategory[k]! / c().total
                                  : null,
                                locale(),
                              )}
                            </td>
                          </tr>
                        )}
                      </For>
                      <tr>
                        <td>
                          <b style={{ "font-weight": 600 }}>
                            {t("finance.costs.sum")}
                          </b>
                        </td>
                        <td class="r num">
                          <b>{zl(c().total, 2)}</b>
                        </td>
                        <td />
                      </tr>
                    </tbody>
                  </table>
                </section>
                <section class="card">
                  <div class="card-h">
                    <h2>{t("finance.costs.year", { year })}</h2>
                    <span class="meta">{t("finance.costs.yearMeta")}</span>
                  </div>
                  <div class="card-b">
                    <Chart
                      series={chart()}
                      from={new Date(year, 0, 1)}
                      to={new Date(year + 1, 0, 1)}
                      unit="zł"
                      locale={locale()}
                      bucketMs={30 * 86_400_000}
                      emptyText={t("finance.costs.noData")}
                      height={180}
                    />
                  </div>
                </section>
              </div>
              <section class="card">
                <div class="card-h">
                  <h2>{t("finance.costs.byPoint")}</h2>
                </div>
                <div class="scrollx">
                  <table class="tbl">
                    <thead>
                      <tr>
                        <th>{t("finance.terms.point")}</th>
                        <th class="r">{t("finance.costs.quantity")}</th>
                        <th class="r">{t("finance.costs.total")}</th>
                        <th>{t("finance.costs.basis")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <For each={c().points}>
                        {(p) => (
                          <tr
                            class={`click${detail()?.billingPoint.id === p.billingPoint.id ? " sel" : ""}`}
                            onClick={() => setSelected(p.billingPoint.id)}
                          >
                            <td>
                              <MediumBadge medium={p.medium} />{" "}
                              <b style={{ "font-weight": 500 }}>
                                {p.billingPoint.name}
                              </b>
                              <div class="hint">
                                {p.billingPoint.identifier ?? ""}
                              </div>
                            </td>
                            <td class="r num">
                              {quantity(p.estimate.energyKwh, p.estimate.unit)}
                              <Show
                                when={
                                  p.medium === "gas" &&
                                  p.estimate.volumeM3 !== null
                                }
                              >
                                <div class="hint">
                                  {quantity(p.estimate.volumeM3!, "m3")}
                                </div>
                              </Show>
                            </td>
                            <td class="r num">
                              {zl(p.invoice?.net ?? p.estimate.total, 2)}
                            </td>
                            <td>
                              <span class={`chip ${p.invoice ? "ok" : ""}`}>
                                {p.invoice
                                  ? t("finance.costs.fromInvoice")
                                  : t("finance.costs.fromEstimate")}
                              </span>
                              <Show when={p.estimate.uncoveredKwh > 0}>
                                <div
                                  class="hint"
                                  style={{ color: "var(--warn)" }}
                                >
                                  {t("finance.costs.uncovered", {
                                    kwh: quantity(
                                      p.estimate.uncoveredKwh,
                                      p.estimate.unit,
                                    ),
                                  })}
                                </div>
                              </Show>
                            </td>
                          </tr>
                        )}
                      </For>
                    </tbody>
                  </table>
                </div>
              </section>
              <Show when={detail()}>
                {(d) => (
                  <section class="card">
                    <div class="card-h">
                      <h2>
                        {t("finance.costs.lines", {
                          name: d().billingPoint.name,
                        })}
                      </h2>
                      <span class="meta">
                        {t("finance.costs.completenessOf", {
                          share: formatShare(
                            d().estimate.completeness,
                            locale(),
                          ),
                        })}
                      </span>
                    </div>
                    <Lines estimate={d().estimate} />
                    <Allocation billingPointId={d().billingPoint.id} />
                  </section>
                )}
              </Show>
              <p class="hint" style={{ margin: 0 }}>
                {t("finance.costs.hint")}
              </p>
            </>
          )}
        </Show>
      </Show>
    </Show>
  );
}

/**
 * The selected billing point's cost split over the sub-meters and loads it feeds: each one's own hours at the
 * hour's price, fixed fees by its quantity, and the rest no sub-meter carries.
 */
function Allocation(props: { billingPointId: string }) {
  const [split] = createResource(
    () => ({ id: props.billingPointId, ...range() }),
    (q) => api.finance.allocation(q.id, q.from, q.to),
  );
  return (
    <div class="card-b" style={{ "border-top": "1px solid var(--line)" }}>
      <div class="eyebrow" style={{ "margin-bottom": "6px" }}>
        {t("finance.allocation.title")}
      </div>
      <Show when={!split.error} fallback={<ErrorBox error={split.error} />}>
        <Show when={split()} fallback={<Skeleton lines={2} />}>
          {(s) => (
            <Show
              when={s().points.length > 0}
              fallback={
                <p class="hint" style={{ margin: 0 }}>
                  {t("finance.allocation.none")}
                </p>
              }
            >
              <div class="scrollx">
                <table class="tbl">
                  <thead>
                    <tr>
                      <th>{t("finance.allocation.point")}</th>
                      <th class="r">{t("finance.costs.quantity")}</th>
                      <th class="r">{t("finance.allocation.variable")}</th>
                      <th class="r">{t("finance.allocation.fixed")}</th>
                      <th class="r">{t("finance.costs.total")}</th>
                      <th class="r">{t("finance.allocation.share")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <For each={s().points}>
                      {(p) => (
                        <tr>
                          <td>{p.name}</td>
                          <td class="r num">
                            {quantity(p.quantity, s().unit)}
                          </td>
                          <td class="r num">{zl(p.variable, 2)}</td>
                          <td class="r num">{zl(p.fixed, 2)}</td>
                          <td class="r num">{zl(p.total, 2)}</td>
                          <td class="r hint">
                            {formatShare(p.share, locale())}
                          </td>
                        </tr>
                      )}
                    </For>
                    <tr>
                      <td>
                        <b style={{ "font-weight": 500 }}>
                          {t("finance.allocation.unassigned")}
                        </b>
                      </td>
                      <td />
                      <td />
                      <td />
                      <td
                        class="r num"
                        style={{
                          color: s().unassigned < 0 ? "var(--crit)" : undefined,
                        }}
                      >
                        {zl(s().unassigned, 2)}
                      </td>
                      <td class="r hint">
                        {formatShare(s().unassignedShare, locale())}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p class="hint" style={{ margin: "6px 0 0" }}>
                {s().unassigned < 0
                  ? t("finance.allocation.negative")
                  : t("finance.allocation.hint")}
              </p>
            </Show>
          )}
        </Show>
      </Show>
    </div>
  );
}

function Lines(props: { estimate: CostBreakdown }) {
  return (
    <div class="scrollx">
      <table class="tbl">
        <thead>
          <tr>
            <th>{t("finance.lines.component")}</th>
            <th class="r">{t("finance.lines.quantity")}</th>
            <th class="r">{t("finance.lines.price")}</th>
            <th class="r">{t("finance.lines.amount")}</th>
          </tr>
        </thead>
        <tbody>
          <For each={props.estimate.lines}>
            {(l) => (
              <tr>
                <td>
                  {t(`finance.line.${l.key}`)}
                  <Show when={l.zone && l.zone !== "all"}>
                    <span class="hint"> · {t(`finance.zone.${l.zone}`)}</span>
                  </Show>
                </td>
                <td class="r num">
                  {formatNumber(l.quantity, locale(), l.unit === "kWh" ? 0 : 2)}{" "}
                  {t(`finance.unit.${l.unit}`)}
                </td>
                <td class="r num">{formatNumber(l.price, locale(), 4)}</td>
                <td class="r num">{zl(l.amount, 2)}</td>
              </tr>
            )}
          </For>
        </tbody>
      </table>
    </div>
  );
}

/**
 * Zone hours to edit: four days of 24 hours; clicking an hour moves it to the group's next zone. The legend says which
 * colour is which zone.
 */
function ZoneEditor(props: {
  schedule: ZoneSchedule;
  zones: string[];
  onChange: (s: ZoneSchedule) => void;
}) {
  const click = (day: (typeof DAYS)[number], hour: number) =>
    props.onChange({
      ...props.schedule,
      [day]: props.schedule[day].map((z, h) =>
        h === hour ? nextZone(props.zones, z) : z,
      ),
    });
  return (
    <div style={{ display: "grid", gap: "6px" }}>
      <For each={DAYS}>
        {(d) => (
          <div
            style={{
              display: "grid",
              "grid-template-columns": "130px 1fr",
              gap: "8px",
              "align-items": "center",
            }}
          >
            <span class="hint">{t(`finance.terms.day.${d}`)}</span>
            <div
              role="group"
              aria-label={t(`finance.terms.day.${d}`)}
              style={{
                display: "grid",
                "grid-template-columns": "repeat(24, 1fr)",
                gap: "1px",
              }}
            >
              <For each={props.schedule[d]}>
                {(z, h) => (
                  <button
                    type="button"
                    class="zone-hour"
                    style={{ background: ZONE_COLOR[z] ?? "var(--muted)" }}
                    title={`${String(h()).padStart(2, "0")}:00–${String(h() + 1).padStart(2, "0")}:00 · ${t(`finance.zone.${z}`)}`}
                    aria-label={`${String(h()).padStart(2, "0")}:00 ${t(`finance.zone.${z}`)}`}
                    onClick={() => click(d, h())}
                  >
                    {h() % 6 === 0 ? h() : ""}
                  </button>
                )}
              </For>
            </div>
          </div>
        )}
      </For>
      <div
        class="hint"
        style={{ display: "flex", gap: "12px", "flex-wrap": "wrap" }}
      >
        <For each={props.zones}>
          {(z) => (
            <span>
              <span
                style={{
                  display: "inline-block",
                  width: "10px",
                  height: "10px",
                  "border-radius": "2px",
                  background: ZONE_COLOR[z] ?? "var(--muted)",
                  "margin-right": "4px",
                }}
              />
              {t(`finance.zone.${z}`)}
            </span>
          )}
        </For>
        <span>· {t("finance.terms.zoneEditHint")}</span>
      </div>
    </div>
  );
}

function ZoneStrip(props: { hours: string[] }) {
  return (
    <div
      style={{
        display: "flex",
        height: "18px",
        "border-radius": "4px",
        overflow: "hidden",
      }}
    >
      <For each={zoneRuns(props.hours)}>
        {([a, b, z]) => (
          <span
            title={`${String(a).padStart(2, "0")}:00–${String(b).padStart(2, "0")}:00 · ${t(`finance.zone.${z}`)}`}
            style={{
              flex: String(b - a),
              background: ZONE_COLOR[z] ?? "var(--muted)",
              opacity: 0.75,
              color: "#fff",
              "font-size": "10px",
              display: "grid",
              "place-items": "center",
            }}
          >
            {b - a >= 3 ? t(`finance.zoneShort.${z}`) : ""}
          </span>
        )}
      </For>
    </div>
  );
}

type Group = {
  key: string;
  medium: string;
  zones: string[];
  winter: string[];
  summer: string[];
  dayOff: string[];
  summerDayOff: string[];
};

const DAYS = [
  "winterWorkday",
  "summerWorkday",
  "winterDayOff",
  "summerDayOff",
] as const;

/** The group's default zone hours as a schedule. */
const defaultSchedule = (g: Group): ZoneSchedule => ({
  winterWorkday: g.winter,
  summerWorkday: g.summer,
  winterDayOff: g.dayOff,
  summerDayOff: g.summerDayOff,
});

/** Zone hours of a version: its own, else its group's defaults. */
const scheduleOf = (
  v: TariffVersion,
  g: Group | undefined,
): ZoneSchedule | null => v.zones ?? (g ? defaultSchedule(g) : null);

function Terms(props: {
  points: BillingPoint[];
  versions: TariffVersion[];
  groups: Group[];
  loading: boolean;
  onChanged: () => void;
}) {
  const [selected, setSelected] = createSignal<string>();
  const [pointDialog, setPointDialog] = createSignal<{
    point?: BillingPoint;
  }>();
  const [versionDialog, setVersionDialog] = createSignal<{
    point: BillingPoint;
    version?: TariffVersion;
    base?: TariffVersion;
  }>();
  const [removing, setRemoving] = createSignal<
    | { kind: "point"; point: BillingPoint }
    | { kind: "version"; version: TariffVersion }
  >();
  const current = () =>
    props.points.find((p) => p.id === selected()) ?? props.points[0];
  const versionsOf = (id: string) =>
    props.versions
      .filter((v) => v.billingPointId === id)
      .sort((a, b) => b.validFrom.localeCompare(a.validFrom));
  const pointName = (id: string) =>
    siteTree()?.points.find((p) => p.id === id)?.name ?? id;
  const group = (key: string) => props.groups.find((g) => g.key === key);
  const mediumOf = (bp: BillingPoint) => mediumOfPoint(bp.pointId);

  return (
    <>
      <div class="grid g-2 g-2s">
        <section class="card">
          <div class="card-h">
            <h2>{t("finance.terms.points")}</h2>
            <Show when={operator()}>
              <button
                type="button"
                class="btn sm"
                style={{ "margin-left": "auto" }}
                onClick={() => setPointDialog({})}
              >
                <Icon name="plus" />
                {t("finance.terms.addPoint")}
              </button>
            </Show>
          </div>
          <Show
            when={props.points.length > 0}
            fallback={
              <div class="card-b">
                <p class="muted" style={{ margin: 0 }}>
                  {props.loading ? "…" : t("finance.terms.none")}
                </p>
              </div>
            }
          >
            <table class="tbl">
              <tbody>
                <For each={props.points}>
                  {(p) => {
                    const v = () => versionsOf(p.id)[0];
                    return (
                      <tr
                        class={`click${current()?.id === p.id ? " sel" : ""}`}
                        onClick={() => setSelected(p.id)}
                      >
                        <td>
                          <MediumBadge medium={mediumOf(p)} />{" "}
                          <b style={{ "font-weight": 500 }}>{p.name}</b>
                          <div class="hint">
                            {[p.identifier, pointName(p.pointId)]
                              .filter(Boolean)
                              .join(" · ")}
                          </div>
                        </td>
                        <td>{v() ? groupLabel(v()!.group) : "—"}</td>
                        <td class="num">{v()?.validFrom ?? ""}</td>
                      </tr>
                    );
                  }}
                </For>
              </tbody>
            </table>
          </Show>
          <div class="card-b hint">{t("finance.terms.subHint")}</div>
        </section>
        <Show when={current()}>
          {(p) => {
            const vs = () => versionsOf(p().id);
            const v = () => vs()[0];
            return (
              <section class="card">
                <div class="card-h">
                  <div>
                    <div class="eyebrow">
                      {[p().identifier, p().operator, p().seller]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                    <h2 style={{ "font-size": "16px" }}>{p().name}</h2>
                  </div>
                  <Show when={operator()}>
                    <div
                      style={{
                        "margin-left": "auto",
                        display: "flex",
                        gap: "6px",
                        "flex-wrap": "wrap",
                      }}
                    >
                      <button
                        type="button"
                        class="btn sm"
                        onClick={() =>
                          setVersionDialog({ point: p(), base: v() })
                        }
                      >
                        <Icon name="calendar" />
                        {t("finance.terms.newVersion")}
                      </button>
                      <Show when={v()}>
                        <button
                          type="button"
                          class="btn sm"
                          onClick={() =>
                            setVersionDialog({ point: p(), version: v() })
                          }
                        >
                          <Icon name="alert" />
                          {t("finance.terms.correct")}
                        </button>
                      </Show>
                      <button
                        type="button"
                        class="btn ghost sm"
                        title={t("common.edit")}
                        aria-label={t("common.edit")}
                        onClick={() => setPointDialog({ point: p() })}
                      >
                        <Icon name="edit" />
                      </button>
                      <button
                        type="button"
                        class="btn ghost sm"
                        title={t("common.delete")}
                        aria-label={t("common.delete")}
                        onClick={() =>
                          setRemoving({ kind: "point", point: p() })
                        }
                      >
                        <Icon name="x" />
                      </button>
                    </div>
                  </Show>
                </div>
                <div class="card-b" style={{ display: "grid", gap: "14px" }}>
                  <Show
                    when={v()}
                    fallback={
                      <p class="muted" style={{ margin: 0 }}>
                        {t("finance.terms.noVersion")}
                      </p>
                    }
                  >
                    {(cur) => (
                      <>
                        <div>
                          <div
                            class="eyebrow"
                            style={{ "margin-bottom": "6px" }}
                          >
                            {t("finance.terms.versions")}
                          </div>
                          <table class="tbl">
                            <tbody>
                              <For each={vs()}>
                                {(x, i) => (
                                  <tr>
                                    <td class="num">
                                      {x.validFrom} –{" "}
                                      {i() === 0
                                        ? t("finance.terms.now")
                                        : vs()[i() - 1]!.validFrom}
                                    </td>
                                    <td>
                                      {groupLabel(x.group)} · {x.contract}
                                    </td>
                                    <td class="hint">
                                      {x.createdBy} ·{" "}
                                      {formatDateTime(x.createdAt, locale())}
                                    </td>
                                    <td>
                                      <Show when={operator()}>
                                        <button
                                          type="button"
                                          class="btn ghost sm"
                                          aria-label={t("common.delete")}
                                          title={t("common.delete")}
                                          onClick={() =>
                                            setRemoving({
                                              kind: "version",
                                              version: x,
                                            })
                                          }
                                        >
                                          <Icon name="x" />
                                        </button>
                                      </Show>
                                    </td>
                                  </tr>
                                )}
                              </For>
                            </tbody>
                          </table>
                        </div>
                        <dl class="kv" style={{ margin: 0 }}>
                          <dt>{t("finance.terms.group")}</dt>
                          <dd>{groupLabel(cur().group)}</dd>
                          <Show when={cur().contractedPowerKw !== null}>
                            <dt>
                              {t(`finance.terms.powerOf.${mediumOf(p())}`)}
                            </dt>
                            <dd class="num">
                              {cur().contractedPowerKw}{" "}
                              {mediumOf(p()) === "gas" ? "kWh/h" : "kW"}
                            </dd>
                          </Show>
                          <Show when={cur().prices.kwhPerM3}>
                            <dt>{t("finance.terms.field.kwhPerM3")}</dt>
                            <dd class="num">
                              {formatNumber(
                                cur().prices.kwhPerM3!,
                                locale(),
                                3,
                              )}{" "}
                              kWh/m³
                            </dd>
                          </Show>
                          <dt>{t("finance.terms.measured")}</dt>
                          <dd>{pointName(p().pointId)}</dd>
                        </dl>
                        <PriceTable version={cur()} medium={mediumOf(p())} />
                        <Show
                          when={
                            mediumOf(p()) === "el"
                              ? scheduleOf(cur(), group(cur().group))
                              : undefined
                          }
                        >
                          {(sch) => (
                            <div>
                              <div
                                class="eyebrow"
                                style={{ "margin-bottom": "6px" }}
                              >
                                {t("finance.terms.zones", {
                                  group: cur().group,
                                })}{" "}
                                <span class="chip">
                                  {cur().zones
                                    ? t("finance.terms.zonesOwn")
                                    : t("finance.terms.zonesDefault")}
                                </span>
                              </div>
                              <div
                                style={{
                                  display: "grid",
                                  "grid-template-columns": "120px 1fr",
                                  gap: "6px 10px",
                                  "align-items": "center",
                                  "font-size": "12px",
                                }}
                              >
                                <For each={DAYS}>
                                  {(d) => (
                                    <>
                                      <span class="hint">
                                        {t(`finance.terms.day.${d}`)}
                                      </span>
                                      <ZoneStrip hours={sch()[d]} />
                                    </>
                                  )}
                                </For>
                                <span />
                                <div
                                  class="hint num"
                                  style={{
                                    display: "flex",
                                    "justify-content": "space-between",
                                  }}
                                >
                                  <span>00</span>
                                  <span>06</span>
                                  <span>12</span>
                                  <span>18</span>
                                  <span>24</span>
                                </div>
                              </div>
                              <p class="hint" style={{ margin: "6px 0 0" }}>
                                {t("finance.terms.zonesHint")}
                              </p>
                            </div>
                          )}
                        </Show>
                      </>
                    )}
                  </Show>
                  <p class="hint" style={{ margin: 0 }}>
                    {t("finance.terms.versionHint")}
                  </p>
                </div>
              </section>
            );
          }}
        </Show>
      </div>
      <Show when={pointDialog()}>
        {(d) => (
          <BillingPointDialog
            point={d().point}
            onClose={() => setPointDialog(undefined)}
            onSaved={(p) => {
              setSelected(p.id);
              props.onChanged();
            }}
          />
        )}
      </Show>
      <Show when={versionDialog()}>
        {(d) => (
          <TariffDialog
            point={d().point}
            version={d().version}
            base={d().base}
            groups={props.groups}
            onClose={() => setVersionDialog(undefined)}
            onSaved={props.onChanged}
          />
        )}
      </Show>
      <Show when={removing()}>
        {(r) => {
          const x = r();
          return (
            <ConfirmDialog
              title={
                x.kind === "point"
                  ? t("finance.terms.removePoint", { name: x.point.name })
                  : t("finance.terms.removeVersion", {
                      from: x.version.validFrom,
                    })
              }
              text={
                x.kind === "point"
                  ? t("finance.terms.removePointText")
                  : t("finance.terms.removeVersionText")
              }
              confirm={t("common.delete")}
              danger
              onConfirm={async () => {
                await (x.kind === "point"
                  ? api.finance.removeBillingPoint(x.point.id)
                  : api.finance.removeVersion(x.version.id));
                props.onChanged();
              }}
              onClose={() => setRemoving(undefined)}
            />
          );
        }}
      </Show>
    </>
  );
}

function PriceTable(props: { version: TariffVersion; medium: BilledMedium }) {
  const p = () => props.version.prices;
  const rows = (): [string, string, number, string][] => {
    const zoned = (
      key: string,
      prices: Record<string, number>,
      unit: string,
    ): [string, string, number, string][] =>
      Object.entries(prices).map(([z, v]) => [key, z, v, unit]);
    switch (props.medium) {
      case "gas":
        return [
          ...zoned("gasFuel", p().energy, "perKwh"),
          ...zoned("distributionVariable", p().distributionVariable, "perKwh"),
          ["fixedPower", "", p().fixedPerKwMonth, "perKwhhMonth"],
          ["fixedMonthly", "", p().fixedPerMonth, "perMonth"],
        ];
      case "water":
        return [
          ...zoned("water", p().energy, "perM3"),
          ["sewage", "", p().sewagePerM3 ?? 0, "perM3"],
          ["fixedMonthly", "", p().fixedPerMonth, "perMonth"],
        ];
      default:
        return [
          ...(p().marketMarginPerKwh != null
            ? ([
                ["energyMarketMargin", "", p().marketMarginPerKwh!, "perKwh"],
              ] as [string, string, number, string][])
            : []),
          ...zoned(
            p().marketMarginPerKwh != null ? "energyFallback" : "energy",
            p().energy,
            "perKwh",
          ),
          ...zoned("distributionVariable", p().distributionVariable, "perKwh"),
          ["quality", "", p().qualityFee, "perKwh"],
          ["capacity", "", p().capacityFee, "perKwh"],
          ["renewable", "", p().renewableFee, "perKwh"],
          ["fixedPower", "", p().fixedPerKwMonth, "perKwMonth"],
          ["fixedMonthly", "", p().fixedPerMonth, "perMonth"],
          ["reactive", "", p().reactivePerKvarh, "perKvarh"],
          [
            p().excessPerKw == null ? "excessDefault" : "excess",
            "",
            p().excessPerKw ?? p().fixedPerKwMonth * 10,
            "perKwOver",
          ],
        ];
    }
  };
  return (
    <div class="scrollx">
      <table class="tbl">
        <thead>
          <tr>
            <th>{t("finance.lines.component")}</th>
            <th class="r">{t("finance.lines.price")}</th>
            <th>{t("finance.lines.unit")}</th>
          </tr>
        </thead>
        <tbody>
          <For
            each={rows().filter(
              (r) => r[2] !== 0 || r[0] === "energyMarketMargin",
            )}
          >
            {(r) => (
              <tr>
                <td>
                  {t(`finance.line.${r[0]}`)}
                  <Show when={r[1] && r[1] !== "all"}>
                    <span class="hint"> · {t(`finance.zone.${r[1]}`)}</span>
                  </Show>
                </td>
                <td class="r num">{formatNumber(r[2], locale(), 4)}</td>
                <td class="hint">{t(`finance.unit.${r[3]}`)}</td>
              </tr>
            )}
          </For>
          <Show when={props.medium === "el"}>
            <tr>
              <td>{t("finance.terms.tanPhi")}</td>
              <td class="r num">
                {formatNumber(p().tanPhiThreshold, locale(), 2)}
              </td>
              <td class="hint">tg φ₀</td>
            </tr>
          </Show>
        </tbody>
      </table>
    </div>
  );
}

function Field(props: {
  id: string;
  label: string;
  children: JSX.Element;
  hint?: string;
}) {
  return (
    <div class="field">
      <label for={props.id}>{props.label}</label>
      {props.children}
      <Show when={props.hint}>
        <span class="hint">{props.hint}</span>
      </Show>
    </div>
  );
}

const num = (s: string) => {
  const v = Number(s.replace(",", ".").trim());
  return s.trim() === "" || Number.isNaN(v) ? null : v;
};

function BillingPointDialog(props: {
  point?: BillingPoint;
  onClose: () => void;
  onSaved: (p: BillingPoint) => void;
}) {
  const meters = () =>
    (siteTree()?.points ?? []).filter((p) => billedMedium(p.medium) !== null);
  const [name, setName] = createSignal(props.point?.name ?? "");
  const [pointId, setPointId] = createSignal(
    props.point?.pointId ??
      meters().find((p) => p.role === "main")?.id ??
      meters()[0]?.id ??
      "",
  );
  const [identifier, setIdentifier] = createSignal(
    props.point?.identifier ?? "",
  );
  const [op, setOp] = createSignal(props.point?.operator ?? "");
  const [seller, setSeller] = createSignal(props.point?.seller ?? "");
  const [run, error, busy] = useAction();
  const path = (nodeId: string) =>
    pathTo(siteTree()?.nodes ?? [], nodeId)
      .map((n) => n.name)
      .join(" / ");
  return (
    <Dialog
      title={props.point ? props.point.name : t("finance.terms.addPoint")}
      onClose={props.onClose}
      footer={
        <>
          <button type="button" class="btn" onClick={() => props.onClose()}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            class="btn primary"
            disabled={busy() || !name().trim() || !pointId()}
            onClick={() =>
              void run(async () => {
                const saved = await api.finance.saveBillingPoint(
                  props.point?.id ?? null,
                  {
                    name: name().trim(),
                    pointId: pointId(),
                    identifier: identifier().trim() || null,
                    operator: op().trim() || null,
                    seller: seller().trim() || null,
                  },
                );
                props.onSaved(saved);
                props.onClose();
              })
            }
          >
            {t("common.save")}
          </button>
        </>
      }
    >
      <div style={{ display: "grid", gap: "10px" }}>
        <Field id="bpName" label={t("finance.terms.name")}>
          <input
            id="bpName"
            value={name()}
            placeholder={t("finance.terms.namePlaceholder")}
            onInput={(e) => setName(e.currentTarget.value)}
          />
        </Field>
        <Field
          id="bpPoint"
          label={t("finance.terms.measured")}
          hint={t("finance.terms.measuredHint")}
        >
          <select
            id="bpPoint"
            value={pointId()}
            onChange={(e) => setPointId(e.currentTarget.value)}
          >
            <For each={meters()}>
              {(p) => (
                <option value={p.id}>
                  {t(`medium.${p.medium}`)} · {p.name} · {path(p.nodeId)}
                </option>
              )}
            </For>
          </select>
        </Field>
        <div class="grid g-2e" style={{ gap: "10px" }}>
          <Field id="bpId" label={t("finance.terms.identifier")}>
            <input
              id="bpId"
              value={identifier()}
              placeholder="PPE 590…"
              onInput={(e) => setIdentifier(e.currentTarget.value)}
            />
          </Field>
          <Field id="bpOp" label={t("finance.terms.operator")}>
            <input
              id="bpOp"
              value={op()}
              onInput={(e) => setOp(e.currentTarget.value)}
            />
          </Field>
        </div>
        <Field id="bpSeller" label={t("finance.terms.seller")}>
          <input
            id="bpSeller"
            value={seller()}
            onInput={(e) => setSeller(e.currentTarget.value)}
          />
        </Field>
        <Show when={error()}>
          <ErrorBox error={error()} />
        </Show>
      </div>
    </Dialog>
  );
}

type PriceField =
  | "qualityFee"
  | "capacityFee"
  | "renewableFee"
  | "fixedPerKwMonth"
  | "fixedPerMonth"
  | "reactivePerKvarh"
  | "tanPhiThreshold"
  | "sewagePerM3"
  | "kwhPerM3"
  | "excessPerKw";

/** The billed medium of a metering point of the tree; electricity when it is not known. */
const mediumOfPoint = (pointId: string): BilledMedium =>
  billedMedium(
    siteTree()?.points.find((p) => p.id === pointId)?.medium ?? "el",
  ) ?? "el";

/** A tariff group as people know it; water has one municipal tariff. */
const groupLabel = (key: string) =>
  key === "water" ? t("finance.terms.waterTariff") : key;

/** Gas and water quantities in their own unit: kWh whole, m³ with decimals. */
const quantity = (v: number, unit: string) =>
  `${formatNumber(v, locale(), unit === "kWh" ? 0 : 2)} ${t(`finance.unit.${unit}`)}`;

/**
 * Terms of a billing point: a new version from a date (from the current one as a start), or a correction of the
 * version entered wrongly. Fields start empty for a first version; nothing is filled with made-up prices.
 */
function TariffDialog(props: {
  point: BillingPoint;
  version?: TariffVersion;
  base?: TariffVersion;
  groups: Group[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const from = props.version ?? props.base;
  const medium = mediumOfPoint(props.point.pointId);
  const groups = () => props.groups.filter((g) => g.medium === medium);
  const [validFrom, setValidFrom] = createSignal(
    props.version?.validFrom ?? today(),
  );
  const [group, setGroup] = createSignal(
    from?.group ?? { el: "C11", gas: "W-3.6", water: "water" }[medium],
  );
  const [contract, setContract] = createSignal(
    from?.contract ?? t("finance.terms.contractDefault"),
  );
  const [power, setPower] = createSignal(
    from?.contractedPowerKw == null ? "" : String(from.contractedPowerKw),
  );
  const str = (v: number | undefined) => (v === undefined ? "" : String(v));
  const [energy, setEnergy] = createSignal<Record<string, string>>(
    Object.fromEntries(
      Object.entries(from?.prices.energy ?? {}).map(([k, v]) => [k, String(v)]),
    ),
  );
  const [dist, setDist] = createSignal<Record<string, string>>(
    Object.fromEntries(
      Object.entries(from?.prices.distributionVariable ?? {}).map(([k, v]) => [
        k,
        String(v),
      ]),
    ),
  );
  const [fees, setFees] = createSignal<Record<PriceField, string>>({
    qualityFee: str(from?.prices.qualityFee),
    capacityFee: str(from?.prices.capacityFee),
    renewableFee: str(from?.prices.renewableFee),
    fixedPerKwMonth: str(from?.prices.fixedPerKwMonth),
    fixedPerMonth: str(from?.prices.fixedPerMonth),
    reactivePerKvarh: str(from?.prices.reactivePerKvarh),
    tanPhiThreshold: str(from?.prices.tanPhiThreshold ?? 0.4),
    sewagePerM3: str(from?.prices.sewagePerM3),
    kwhPerM3: str(from?.prices.kwhPerM3 ?? undefined),
    excessPerKw: str(from?.prices.excessPerKw ?? undefined),
  });
  const groupOf = () => props.groups.find((g) => g.key === group());
  // Own zone hours start from the version's, else from the group's defaults; changing the group starts over.
  const [ownZones, setOwnZones] = createSignal(!!from?.zones);
  // Dynamic tariff: energy at the TGE price of each hour plus the seller's margin.
  const [dynamic, setDynamic] = createSignal(
    from?.prices.marketMarginPerKwh != null,
  );
  const [margin, setMargin] = createSignal(
    str(from?.prices.marketMarginPerKwh ?? undefined),
  );
  const [schedule, setSchedule] = createSignal<ZoneSchedule | null>(
    from?.zones ?? null,
  );
  const currentSchedule = () =>
    schedule() ?? (groupOf() ? defaultSchedule(groupOf()!) : null);
  const changeGroup = (key: string) => {
    setGroup(key);
    setSchedule(null);
  };
  const fields = () =>
    [
      ...PRICE_FIELDS_OF[medium],
      ...(medium === "gas" ? ["kwhPerM3"] : []),
    ] as PriceField[];
  // Water has no separate distribution price per m³: sewage and the subscription carry it.
  const distributed = medium !== "water";
  const [note, setNote] = createSignal(props.version?.note ?? "");
  const [problem, setProblem] = createSignal<string>();
  const [run, error, busy] = useAction();
  const zones = () =>
    props.groups.find((g) => g.key === group())?.zones ?? ["all"];

  const save = () => {
    const prices: TariffPrices = {
      energy: Object.fromEntries(
        zones().map((z) => [z, num(energy()[z] ?? "") ?? NaN]),
      ),
      distributionVariable: Object.fromEntries(
        zones().map((z) => [
          z,
          distributed ? (num(dist()[z] ?? "") ?? NaN) : 0,
        ]),
      ),
      qualityFee: num(fees().qualityFee) ?? 0,
      capacityFee: num(fees().capacityFee) ?? 0,
      renewableFee: num(fees().renewableFee) ?? 0,
      fixedPerKwMonth: num(fees().fixedPerKwMonth) ?? 0,
      fixedPerMonth: num(fees().fixedPerMonth) ?? 0,
      reactivePerKvarh: num(fees().reactivePerKvarh) ?? 0,
      tanPhiThreshold:
        medium === "el" ? (num(fees().tanPhiThreshold) ?? 0.4) : 0,
      sewagePerM3: num(fees().sewagePerM3) ?? 0,
      kwhPerM3: medium === "gas" ? num(fees().kwhPerM3) : null,
      excessPerKw: medium === "el" ? num(fees().excessPerKw) : null,
      marketMarginPerKwh:
        medium === "el" && dynamic() ? (num(margin()) ?? 0) : null,
    };
    if (medium === "gas" && !(prices.kwhPerM3 && prices.kwhPerM3 > 0)) {
      setProblem(t("finance.terms.needKwhPerM3"));
      return;
    }
    if (
      [
        ...Object.values(prices.energy),
        ...Object.values(prices.distributionVariable),
      ].some((v) => Number.isNaN(v))
    ) {
      setProblem(t("finance.terms.needZonePrices"));
      return;
    }
    setProblem(undefined);
    const input: TariffInput = {
      validFrom: validFrom(),
      group: group(),
      contract: contract().trim(),
      contractedPowerKw: num(power()),
      prices,
      note: note().trim() || null,
      zones: medium === "el" && ownZones() ? currentSchedule() : null,
    };
    void run(async () => {
      await api.finance.saveVersion(
        props.point.id,
        props.version?.id ?? null,
        input,
      );
      props.onSaved();
      props.onClose();
    });
  };

  return (
    <Dialog
      title={
        props.version
          ? t("finance.terms.correctTitle", { name: props.point.name })
          : t("finance.terms.newVersionTitle", { name: props.point.name })
      }
      wide
      onClose={props.onClose}
      footer={
        <>
          <button type="button" class="btn" onClick={() => props.onClose()}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            class="btn primary"
            disabled={busy()}
            onClick={save}
          >
            {t("common.save")}
          </button>
        </>
      }
    >
      <div style={{ display: "grid", gap: "12px" }}>
        <Show when={props.version}>
          <p class="chip warn" style={{ margin: 0, "justify-self": "start" }}>
            {t("finance.terms.correctWarning")}
          </p>
        </Show>
        <div class="grid g-2e" style={{ gap: "10px" }}>
          <Field id="tvFrom" label={t("finance.terms.validFrom")}>
            <input
              id="tvFrom"
              type="date"
              value={validFrom()}
              onInput={(e) => setValidFrom(e.currentTarget.value)}
            />
          </Field>
          <Field id="tvGroup" label={t("finance.terms.group")}>
            <select
              id="tvGroup"
              value={group()}
              onChange={(e) => changeGroup(e.currentTarget.value)}
            >
              <For each={groups()}>
                {(g) => <option value={g.key}>{groupLabel(g.key)}</option>}
              </For>
            </select>
          </Field>
        </div>
        <div class="grid g-2e" style={{ gap: "10px" }}>
          <Field id="tvContract" label={t("finance.terms.contract")}>
            <input
              id="tvContract"
              value={contract()}
              onInput={(e) => setContract(e.currentTarget.value)}
            />
          </Field>
          <Show when={medium !== "water"}>
            <Field id="tvPower" label={t(`finance.terms.powerOf.${medium}`)}>
              <input
                id="tvPower"
                inputmode="decimal"
                value={power()}
                placeholder={medium === "gas" ? "kWh/h" : "kW"}
                onInput={(e) => setPower(e.currentTarget.value)}
              />
            </Field>
          </Show>
        </div>
        <div>
          <div class="eyebrow" style={{ "margin-bottom": "6px" }}>
            {t("finance.terms.zonePrices")}
          </div>
          <table class="tbl">
            <thead>
              <tr>
                <th>{t("finance.terms.zone")}</th>
                <th>
                  {t(
                    `finance.line.${{ el: "energy", gas: "gasFuel", water: "water" }[medium]}`,
                  )}{" "}
                  (
                  {t(`finance.unit.${medium === "water" ? "perM3" : "perKwh"}`)}
                  )
                </th>
                <Show when={distributed}>
                  <th>
                    {t("finance.line.distributionVariable")} (
                    {t("finance.unit.perKwh")})
                  </th>
                </Show>
              </tr>
            </thead>
            <tbody>
              <For each={zones()}>
                {(z) => (
                  <tr>
                    <td>{t(`finance.zone.${z}`)}</td>
                    <td>
                      <input
                        inputmode="decimal"
                        aria-label={`${t("finance.line.energy")} · ${t(`finance.zone.${z}`)}`}
                        value={energy()[z] ?? ""}
                        onInput={(e) =>
                          setEnergy({ ...energy(), [z]: e.currentTarget.value })
                        }
                      />
                    </td>
                    <Show when={distributed}>
                      <td>
                        <input
                          inputmode="decimal"
                          aria-label={`${t("finance.line.distributionVariable")} · ${t(`finance.zone.${z}`)}`}
                          value={dist()[z] ?? ""}
                          onInput={(e) =>
                            setDist({ ...dist(), [z]: e.currentTarget.value })
                          }
                        />
                      </td>
                    </Show>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
        <div class="grid g-2e" style={{ gap: "10px" }}>
          <For each={fields()}>
            {(f) => (
              <Field
                id={`tv-${f}`}
                label={t(
                  f === "fixedPerKwMonth" && medium === "gas"
                    ? "finance.terms.field.fixedPerKwhhMonth"
                    : `finance.terms.field.${f}`,
                )}
              >
                <input
                  id={`tv-${f}`}
                  inputmode="decimal"
                  value={fees()[f]}
                  onInput={(e) =>
                    setFees({ ...fees(), [f]: e.currentTarget.value })
                  }
                />
              </Field>
            )}
          </For>
        </div>
        <Show when={medium === "el"}>
          <div>
            <label class="opt" style={{ "margin-bottom": "6px" }}>
              <input
                type="checkbox"
                checked={dynamic()}
                onChange={(e) => setDynamic(e.currentTarget.checked)}
              />
              <div>
                <b>{t("finance.terms.dynamic")}</b>
                <small>{t("finance.terms.dynamicHint")}</small>
              </div>
            </label>
            <Show when={dynamic()}>
              <Field
                id="tfMargin"
                label={t("finance.terms.margin")}
                hint={t("finance.terms.marginHint")}
              >
                <input
                  id="tfMargin"
                  inputmode="decimal"
                  value={margin()}
                  onInput={(e) => setMargin(e.currentTarget.value)}
                />
              </Field>
            </Show>
          </div>
        </Show>
        <Show when={medium === "el" && currentSchedule()}>
          {(sch) => (
            <div>
              <label class="opt" style={{ "margin-bottom": "6px" }}>
                <input
                  type="checkbox"
                  checked={ownZones()}
                  onChange={(e) => setOwnZones(e.currentTarget.checked)}
                />
                <div>
                  <b>{t("finance.terms.ownZones")}</b>
                  <small>{t("finance.terms.ownZonesHint")}</small>
                </div>
              </label>
              <Show when={ownZones()}>
                <ZoneEditor
                  schedule={sch()}
                  zones={zones()}
                  onChange={setSchedule}
                />
              </Show>
            </div>
          )}
        </Show>
        <Field id="tvNote" label={t("finance.terms.note")}>
          <input
            id="tvNote"
            value={note()}
            onInput={(e) => setNote(e.currentTarget.value)}
          />
        </Field>
        <p class="hint" style={{ margin: 0 }}>
          {t(`finance.terms.mediumHint.${medium}`)}
        </p>
        <Show when={problem()}>
          <p
            class="chip warn"
            role="alert"
            style={{ margin: 0, "justify-self": "start" }}
          >
            {problem()}
          </p>
        </Show>
        <Show when={error()}>
          <ErrorBox error={error()} />
        </Show>
      </div>
    </Dialog>
  );
}

function BudgetChart(props: { months: MonthCost[]; year: number }) {
  const at = (m: number) => new Date(props.year, m - 1, 1).getTime();
  const series = (): ChartSeries[] => [
    {
      key: "plan",
      label: t("finance.costs.plan"),
      color: "var(--line-2)",
      kind: "bar",
      points: props.months.map((m) => ({ t: at(m.month), v: m.plan })),
    },
    {
      key: "cost",
      label: t("finance.costs.cost"),
      color: "var(--accent)",
      kind: "bar",
      points: props.months.map((m) => ({ t: at(m.month), v: m.cost })),
    },
  ];
  return (
    <Chart
      series={series()}
      from={new Date(props.year, 0, 1)}
      to={new Date(props.year + 1, 0, 1)}
      unit="zł"
      locale={locale()}
      bucketMs={30 * 86_400_000}
      emptyText={t("finance.costs.noData")}
      height={180}
    />
  );
}

function Budgets() {
  const [budgets, { refetch }] = createResource(() => api.finance.budgets());
  const [selected, setSelected] = createSignal<string>();
  const [editing, setEditing] = createSignal<{ status?: BudgetStatus }>();
  const [removing, setRemoving] = createSignal<BudgetStatus>();
  const reload = () => {
    api.invalidate("/api/v1/finance");
    void refetch();
  };
  const current = () =>
    (budgets() ?? []).find((b) => b.budget.id === selected()) ?? budgets()?.[0];
  const nodeName = (id: string) =>
    siteTree()?.nodes.find((n) => n.id === id)?.name ?? "";
  return (
    <>
      <section class="card">
        <div class="card-h">
          <h2>{t("finance.budgets.list")}</h2>
          <Show when={operator()}>
            <button
              type="button"
              class="btn sm primary"
              style={{ "margin-left": "auto" }}
              onClick={() => setEditing({})}
            >
              <Icon name="plus" />
              {t("finance.budgets.new")}
            </button>
          </Show>
        </div>
        <Show
          when={!budgets.error}
          fallback={<ErrorBox error={budgets.error} />}
        >
          <Show
            when={(budgets() ?? []).length > 0}
            fallback={
              <div class="card-b">
                <p class="muted" style={{ margin: 0 }}>
                  {budgets.loading ? "…" : t("finance.budgets.none")}
                </p>
              </div>
            }
          >
            <div class="scrollx">
              <table class="tbl">
                <thead>
                  <tr>
                    <th>{t("finance.budgets.name")}</th>
                    <th>{t("finance.budgets.scope")}</th>
                    <th class="r">{t("finance.costs.plan")}</th>
                    <th class="r">{t("finance.budgets.actual")}</th>
                    <th style={{ width: "22%" }}>{t("finance.budgets.use")}</th>
                    <th class="r">{t("finance.budgets.forecast")}</th>
                    <th>{t("finance.budgets.state")}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  <For each={budgets()}>
                    {(b) => {
                      const plan = b.budget.monthly.reduce((a, x) => a + x, 0);
                      const use = plan > 0 ? b.actual / plan : 0;
                      return (
                        <tr
                          class={`click${current()?.budget.id === b.budget.id ? " sel" : ""}`}
                          onClick={() => setSelected(b.budget.id)}
                        >
                          <td>
                            <b style={{ "font-weight": 500 }}>
                              {b.budget.name}
                            </b>
                            <div class="hint">
                              {b.budget.year} · {b.budget.owner}
                            </div>
                          </td>
                          <td>{nodeName(b.budget.nodeId)}</td>
                          <td class="r num">{zl(plan)}</td>
                          <td class="r num">{zl(b.actual)}</td>
                          <td>
                            <div
                              style={{
                                height: "7px",
                                "border-radius": "4px",
                                background: "var(--sunken)",
                              }}
                            >
                              <div
                                style={{
                                  height: "7px",
                                  "border-radius": "4px",
                                  width: `${Math.min(100, use * 100)}%`,
                                  background: b.overThreshold
                                    ? "var(--crit)"
                                    : "var(--accent)",
                                }}
                              />
                            </div>
                            <div class="hint">
                              {t("finance.budgets.ofPlan", {
                                share: formatShare(use, locale()),
                              })}
                            </div>
                          </td>
                          <td class="r num">{zl(b.forecast)}</td>
                          <td>
                            <span
                              class={`chip ${b.budget.status === "draft" ? "" : b.overThreshold ? "crit" : "ok"}`}
                            >
                              {b.budget.status === "draft"
                                ? t("finance.budgets.draft")
                                : b.overThreshold
                                  ? t("finance.budgets.over", {
                                      threshold: b.budget.thresholdPercent,
                                    })
                                  : t("finance.budgets.within")}
                            </span>
                          </td>
                          <td style={{ "white-space": "nowrap" }}>
                            <Show when={operator()}>
                              <button
                                type="button"
                                class="btn ghost sm"
                                aria-label={t("common.edit")}
                                title={t("common.edit")}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditing({ status: b });
                                }}
                              >
                                <Icon name="edit" />
                              </button>
                              <button
                                type="button"
                                class="btn ghost sm"
                                aria-label={t("common.delete")}
                                title={t("common.delete")}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setRemoving(b);
                                }}
                              >
                                <Icon name="x" />
                              </button>
                            </Show>
                          </td>
                        </tr>
                      );
                    }}
                  </For>
                </tbody>
              </table>
            </div>
          </Show>
        </Show>
      </section>
      <Show when={current()}>
        {(b) => (
          <section class="card">
            <div class="card-h">
              <h2>{t("finance.budgets.chart", { name: b().budget.name })}</h2>
              <span class="meta">{t("finance.budgets.chartMeta")}</span>
            </div>
            <div class="card-b">
              <BudgetChart months={b().months} year={b().budget.year} />
            </div>
            <div class="card-b hint" style={{ "padding-top": 0 }}>
              {t("finance.budgets.forecastHint")}
            </div>
          </section>
        )}
      </Show>
      <Show when={editing()}>
        {(e) => (
          <BudgetDialog
            status={e().status}
            onClose={() => setEditing(undefined)}
            onSaved={reload}
          />
        )}
      </Show>
      <Show when={removing()}>
        {(b) => (
          <ConfirmDialog
            title={t("finance.budgets.remove", { name: b().budget.name })}
            text={t("finance.budgets.removeText")}
            confirm={t("common.delete")}
            danger
            onConfirm={async () => {
              await api.finance.removeBudget(b().budget.id);
              reload();
            }}
            onClose={() => setRemoving(undefined)}
          />
        )}
      </Show>
    </>
  );
}

function BudgetDialog(props: {
  status?: BudgetStatus;
  onClose: () => void;
  onSaved: () => void;
}) {
  const b = props.status?.budget;
  const year = new Date().getFullYear();
  const [name, setName] = createSignal(b?.name ?? "");
  const [nodeId, setNodeId] = createSignal(b?.nodeId ?? scope()?.id ?? "");
  const [budgetYear, setBudgetYear] = createSignal(b?.year ?? year);
  const [monthly, setMonthly] = createSignal<string[]>(
    (b?.monthly ?? Array<number>(12).fill(0)).map((v) => (v ? String(v) : "")),
  );
  const [total, setTotal] = createSignal("");
  const [threshold, setThreshold] = createSignal(
    String(b?.thresholdPercent ?? 95),
  );
  const [status, setStatus] = createSignal<"draft" | "approved">(
    b?.status ?? "approved",
  );
  const [run, error, busy] = useAction();
  const sum = () => monthly().reduce((a, v) => a + (num(v) ?? 0), 0);
  const months = [
    "I",
    "II",
    "III",
    "IV",
    "V",
    "VI",
    "VII",
    "VIII",
    "IX",
    "X",
    "XI",
    "XII",
  ];
  const spread = async (byLastYear: boolean) => {
    const amount = num(total());
    if (amount === null) {
      return;
    }
    const profile = byLastYear
      ? (await api.finance.monthly(nodeId(), budgetYear() - 1)).map(
          (m) => m.cost,
        )
      : undefined;
    setMonthly(spreadBudget(amount, profile).map(String));
  };
  return (
    <Dialog
      title={b ? b.name : t("finance.budgets.new")}
      wide
      onClose={props.onClose}
      footer={
        <>
          <button type="button" class="btn" onClick={() => props.onClose()}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            class="btn primary"
            disabled={busy() || !name().trim() || !nodeId()}
            onClick={() =>
              void run(async () => {
                const input: BudgetInput = {
                  name: name().trim(),
                  nodeId: nodeId(),
                  year: budgetYear(),
                  monthly: monthly().map((v) => num(v) ?? 0),
                  thresholdPercent: num(threshold()) ?? 95,
                  status: status(),
                };
                await api.finance.saveBudget(b?.id ?? null, input);
                props.onSaved();
                props.onClose();
              })
            }
          >
            {t("common.save")}
          </button>
        </>
      }
    >
      <div style={{ display: "grid", gap: "12px" }}>
        <div class="grid g-2e" style={{ gap: "10px" }}>
          <Field id="bdName" label={t("finance.budgets.name")}>
            <input
              id="bdName"
              value={name()}
              placeholder={t("finance.budgets.namePlaceholder")}
              onInput={(e) => setName(e.currentTarget.value)}
            />
          </Field>
          <Field id="bdNode" label={t("finance.budgets.scope")}>
            <select
              id="bdNode"
              value={nodeId()}
              onChange={(e) => setNodeId(e.currentTarget.value)}
            >
              <For each={depthFirst(siteTree()?.nodes ?? [])}>
                {(n) => (
                  <option value={n.node.id}>
                    {"  ".repeat(n.depth)}
                    {n.node.name}
                  </option>
                )}
              </For>
            </select>
          </Field>
        </div>
        <div class="grid g-2e" style={{ gap: "10px" }}>
          <Field id="bdYear" label={t("finance.budgets.year")}>
            <input
              id="bdYear"
              type="number"
              value={budgetYear()}
              onInput={(e) => setBudgetYear(Number(e.currentTarget.value))}
            />
          </Field>
          <Field
            id="bdThr"
            label={t("finance.budgets.threshold")}
            hint={t("finance.budgets.thresholdHint")}
          >
            <input
              id="bdThr"
              inputmode="numeric"
              value={threshold()}
              onInput={(e) => setThreshold(e.currentTarget.value)}
            />
          </Field>
        </div>
        <div
          style={{
            display: "flex",
            gap: "8px",
            "align-items": "end",
            "flex-wrap": "wrap",
          }}
        >
          <Field id="bdTotal" label={t("finance.budgets.total")}>
            <input
              id="bdTotal"
              inputmode="decimal"
              value={total()}
              placeholder="zł"
              onInput={(e) => setTotal(e.currentTarget.value)}
            />
          </Field>
          <button
            type="button"
            class="btn sm"
            onClick={() => void spread(false)}
          >
            {t("finance.budgets.spreadEven")}
          </button>
          <button
            type="button"
            class="btn sm"
            onClick={() => void spread(true)}
          >
            {t("finance.budgets.spreadLastYear")}
          </button>
        </div>
        <div
          style={{
            display: "grid",
            "grid-template-columns": "repeat(auto-fill, minmax(96px, 1fr))",
            gap: "8px",
          }}
        >
          <For each={months}>
            {(m, i) => (
              <Field id={`bdM${i()}`} label={m}>
                <input
                  id={`bdM${i()}`}
                  inputmode="decimal"
                  value={monthly()[i()]}
                  onInput={(e) => {
                    const next = [...monthly()];
                    next[i()] = e.currentTarget.value;
                    setMonthly(next);
                  }}
                />
              </Field>
            )}
          </For>
        </div>
        <p class="hint" style={{ margin: 0 }}>
          {t("finance.budgets.sum", { sum: zl(sum()) })}
        </p>
        <label class="check">
          <input
            type="checkbox"
            checked={status() === "draft"}
            onChange={(e) =>
              setStatus(e.currentTarget.checked ? "draft" : "approved")
            }
          />
          <span>{t("finance.budgets.asDraft")}</span>
        </label>
        <Show when={error()}>
          <ErrorBox error={error()} />
        </Show>
      </div>
    </Dialog>
  );
}

function Invoices(props: {
  points: BillingPoint[];
  invoices: InvoiceCheck[];
  loading: boolean;
  onChanged: () => void;
}) {
  const [selected, setSelected] = createSignal<string>();
  const [adding, setAdding] = createSignal(false);
  const [accepting, setAccepting] = createSignal<InvoiceCheck>();
  const [removing, setRemoving] = createSignal<InvoiceCheck>();
  const current = () =>
    props.invoices.find((i) => i.invoice.id === selected()) ??
    props.invoices[0];
  const pointName = (id: string) =>
    props.points.find((p) => p.id === id)?.name ?? "";
  const pct = (v: number) =>
    `${v > 0 ? "+" : ""}${formatNumber(v * 100, locale(), 1)}%`;
  const chip = (c: InvoiceCheck) =>
    c.invoice.status === "reconciled"
      ? [
          "ok",
          t("finance.invoices.reconciled", { diff: pct(c.differencePercent) }),
        ]
      : c.invoice.status === "accepted"
        ? ["ok", t("finance.invoices.accepted")]
        : [
            "warn",
            t("finance.invoices.toExplain", { diff: pct(c.differencePercent) }),
          ];
  return (
    <>
      <div class="grid g-2 g-2s">
        <section class="card">
          <div class="card-h">
            <h2>{t("finance.invoices.list")}</h2>
            <Show when={operator() && props.points.length > 0}>
              <button
                type="button"
                class="btn sm primary"
                style={{ "margin-left": "auto" }}
                onClick={() => setAdding(true)}
              >
                <Icon name="plus" />
                {t("finance.invoices.add")}
              </button>
            </Show>
          </div>
          <Show
            when={props.invoices.length > 0}
            fallback={
              <div class="card-b">
                <p class="muted" style={{ margin: 0 }}>
                  {props.loading
                    ? "…"
                    : props.points.length === 0
                      ? t("finance.invoices.needPoint")
                      : t("finance.invoices.none")}
                </p>
              </div>
            }
          >
            <table class="tbl">
              <tbody>
                <For each={props.invoices}>
                  {(c) => (
                    <tr
                      class={`click${current()?.invoice.id === c.invoice.id ? " sel" : ""}`}
                      onClick={() => setSelected(c.invoice.id)}
                    >
                      <td>
                        <b style={{ "font-weight": 500 }}>{c.invoice.number}</b>
                        <div class="hint">
                          {pointName(c.invoice.billingPointId)} ·{" "}
                          {c.invoice.periodFrom} – {c.invoice.periodTo}
                        </div>
                      </td>
                      <td class="r num">{zl(c.invoice.net, 2)}</td>
                      <td>
                        <span class={`chip ${chip(c)[0]}`}>{chip(c)[1]}</span>
                      </td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </Show>
        </section>
        <Show when={current()}>
          {(c) => {
            const invoiced = (cat: string) =>
              c().invoice.rows.find((r) => r.category === cat)?.amount ?? null;
            return (
              <section class="card">
                <div class="card-h">
                  <div>
                    <div class="eyebrow">
                      {pointName(c().invoice.billingPointId)} ·{" "}
                      {t("finance.invoices.period", {
                        from: c().invoice.periodFrom,
                        to: c().invoice.periodTo,
                      })}{" "}
                      ·{" "}
                      {t("finance.invoices.issued", {
                        at: c().invoice.issuedOn,
                      })}
                    </div>
                    <h2 style={{ "font-size": "16px" }}>
                      {c().invoice.number}
                    </h2>
                  </div>
                  <span
                    style={{ "margin-left": "auto" }}
                    class={`chip ${chip(c())[0]}`}
                  >
                    {chip(c())[1]}
                  </span>
                </div>
                <div class="scrollx">
                  <table class="tbl">
                    <thead>
                      <tr>
                        <th>{t("finance.invoices.category")}</th>
                        <th class="r">{t("finance.invoices.invoice")}</th>
                        <th class="r">{t("finance.invoices.estimate")}</th>
                        <th class="r">{t("finance.invoices.difference")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <For each={CATEGORIES}>
                        {(cat) => {
                          const est = c().estimate.byCategory[cat] ?? 0;
                          const inv = invoiced(cat);
                          return (
                            <Show when={inv !== null || est !== 0}>
                              <tr>
                                <td>{t(`finance.category.${cat}`)}</td>
                                <td class="r num">{zl(inv, 2)}</td>
                                <td class="r num">{zl(est, 2)}</td>
                                <td class="r num">
                                  {inv === null ? "—" : zl(inv - est, 2)}
                                </td>
                              </tr>
                            </Show>
                          );
                        }}
                      </For>
                      <tr>
                        <td>
                          <b style={{ "font-weight": 600 }}>
                            {t("finance.invoices.net")}
                          </b>
                        </td>
                        <td class="r num">
                          <b>{zl(c().invoice.net, 2)}</b>
                        </td>
                        <td class="r num">
                          <b>{zl(c().estimate.total, 2)}</b>
                        </td>
                        <td class="r num">
                          <b>{zl(c().invoice.net - c().estimate.total, 2)}</b>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <Show when={c().invoice.energyKwh !== null}>
                  <div class="note" style={{ "border-bottom": 0 }}>
                    {t("finance.invoices.energyCompare", {
                      invoice: formatNumber(c().invoice.energyKwh, locale(), 0),
                      metered: formatNumber(
                        c().estimate.energyKwh,
                        locale(),
                        0,
                      ),
                    })}
                  </div>
                </Show>
                <Show when={c().estimate.completeness < 0.999}>
                  <div
                    class="note"
                    style={{ "border-bottom": 0, color: "var(--warn)" }}
                  >
                    <Icon name="alert" />
                    {t("finance.invoices.partial", {
                      share: formatShare(c().estimate.completeness, locale()),
                    })}
                  </div>
                </Show>
                <Show when={c().invoice.explanation}>
                  <div class="note" style={{ "border-bottom": 0 }}>
                    {t("finance.invoices.explanation")}:{" "}
                    {c().invoice.explanation}
                  </div>
                </Show>
                <Show when={operator()}>
                  <div
                    class="card-b"
                    style={{ display: "flex", gap: "8px", "flex-wrap": "wrap" }}
                  >
                    <Show when={c().invoice.status === "toExplain"}>
                      <button
                        type="button"
                        class="btn primary"
                        onClick={() => setAccepting(c())}
                      >
                        <Icon name="check" />
                        {t("finance.invoices.accept")}
                      </button>
                    </Show>
                    <button
                      type="button"
                      class="btn ghost"
                      onClick={() => setRemoving(c())}
                    >
                      {t("common.delete")}
                    </button>
                  </div>
                </Show>
                <div class="card-b hint" style={{ "padding-top": 0 }}>
                  {t("finance.invoices.hint")}
                </div>
              </section>
            );
          }}
        </Show>
      </div>
      <Show when={adding()}>
        <InvoiceDialog
          points={props.points}
          onClose={() => setAdding(false)}
          onSaved={(c) => {
            setSelected(c.invoice.id);
            props.onChanged();
          }}
        />
      </Show>
      <Show when={accepting()}>
        {(c) => (
          <AcceptDialog
            check={c()}
            onClose={() => setAccepting(undefined)}
            onSaved={props.onChanged}
          />
        )}
      </Show>
      <Show when={removing()}>
        {(c) => (
          <ConfirmDialog
            title={t("finance.invoices.remove", { number: c().invoice.number })}
            text={t("finance.invoices.removeText")}
            confirm={t("common.delete")}
            danger
            onConfirm={async () => {
              await api.finance.removeInvoice(c().invoice.id);
              props.onChanged();
            }}
            onClose={() => setRemoving(undefined)}
          />
        )}
      </Show>
    </>
  );
}

function InvoiceDialog(props: {
  points: BillingPoint[];
  onClose: () => void;
  onSaved: (c: InvoiceCheck) => void;
}) {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const last = new Date(now.getFullYear(), now.getMonth(), 0);
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const [point, setPoint] = createSignal(props.points[0]?.id ?? "");
  const medium = () =>
    mediumOfPoint(props.points.find((p) => p.id === point())?.pointId ?? "");
  const [number, setNumber] = createSignal("");
  const [from, setFrom] = createSignal(iso(first));
  const [to, setTo] = createSignal(iso(last));
  const [issued, setIssued] = createSignal(today());
  const [net, setNet] = createSignal("");
  const [kwh, setKwh] = createSignal("");
  const [parts, setParts] = createSignal<Record<string, string>>({});
  const [run, error, busy] = useAction();
  const [draft, setDraft] = createSignal<InvoiceDraft>();
  const [reading, setReading] = createSignal(false);
  const [readError, setReadError] = createSignal<unknown>();
  const text = (v: number) => String(v).replace(".", ",");
  const readPdf = async (file: File) => {
    setReading(true);
    setReadError(undefined);
    try {
      const d = await api.parseInvoicePdf(file);
      setDraft(d);
      if (
        d.billingPointId &&
        props.points.some((p) => p.id === d.billingPointId)
      ) {
        setPoint(d.billingPointId);
      }
      if (d.number) setNumber(d.number);
      if (d.periodFrom) setFrom(d.periodFrom);
      if (d.periodTo) setTo(d.periodTo);
      if (d.issuedOn) setIssued(d.issuedOn);
      if (d.net !== null) setNet(text(d.net));
      if (d.energyKwh !== null) setKwh(text(d.energyKwh));
      if (d.rows.length > 0) {
        setParts(
          Object.fromEntries(d.rows.map((r) => [r.category, text(r.amount)])),
        );
      }
    } catch (e) {
      setReadError(e);
    } finally {
      setReading(false);
    }
  };
  return (
    <Dialog
      title={t("finance.invoices.add")}
      wide
      onClose={props.onClose}
      footer={
        <>
          <button type="button" class="btn" onClick={() => props.onClose()}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            class="btn primary"
            disabled={busy() || !number().trim() || num(net()) === null}
            onClick={() =>
              void run(async () => {
                const total = num(net())!;
                const c = await api.finance.saveInvoice(null, {
                  billingPointId: point(),
                  number: number().trim(),
                  periodFrom: from(),
                  periodTo: to(),
                  issuedOn: issued(),
                  net: total,
                  energyKwh: medium() === "water" ? null : num(kwh()),
                  rows: invoiceRows(
                    total,
                    Object.fromEntries(
                      Object.entries(parts())
                        .filter(([k]) => CATEGORIES_OF[medium()].includes(k))
                        .map(([k, v]) => [k, num(v)]),
                    ),
                  ),
                });
                props.onSaved(c);
                props.onClose();
              })
            }
          >
            {t("common.save")}
          </button>
        </>
      }
    >
      <div style={{ display: "grid", gap: "12px" }}>
        <div class="note">
          <Icon name="file" />
          <span>
            {t("finance.invoices.pdfHint")}
            <Show when={draft()}>
              {(d) => (
                <span
                  class="hint"
                  style={{ display: "block", "margin-top": "4px" }}
                >
                  {d().found.length === 0
                    ? t("finance.invoices.pdfNothing")
                    : t("finance.invoices.pdfFound", {
                        fields: d()
                          .found.map((f) => t(`finance.invoices.pdfField.${f}`))
                          .join(", "),
                      })}
                </span>
              )}
            </Show>
          </span>
          <label
            class="btn sm"
            style={{ "margin-left": "auto", cursor: "pointer" }}
          >
            {reading() ? "…" : t("finance.invoices.pdfRead")}
            <input
              type="file"
              accept="application/pdf,.pdf"
              style={{ display: "none" }}
              disabled={reading()}
              onChange={(e) => {
                const f = e.currentTarget.files?.[0];
                if (f) void readPdf(f);
                e.currentTarget.value = "";
              }}
            />
          </label>
        </div>
        <Show when={readError()}>
          <ErrorBox error={readError()} />
        </Show>
        <div class="grid g-2e" style={{ gap: "10px" }}>
          <Field id="ivPoint" label={t("finance.terms.point")}>
            <select
              id="ivPoint"
              value={point()}
              onChange={(e) => setPoint(e.currentTarget.value)}
            >
              <For each={props.points}>
                {(p) => <option value={p.id}>{p.name}</option>}
              </For>
            </select>
          </Field>
          <Field id="ivNo" label={t("finance.invoices.number")}>
            <input
              id="ivNo"
              value={number()}
              placeholder="FE/2026/10/…"
              onInput={(e) => setNumber(e.currentTarget.value)}
            />
          </Field>
        </div>
        <div class="grid g-2e" style={{ gap: "10px" }}>
          <Field id="ivFrom" label={t("finance.invoices.from")}>
            <input
              id="ivFrom"
              type="date"
              value={from()}
              onInput={(e) => setFrom(e.currentTarget.value)}
            />
          </Field>
          <Field id="ivTo" label={t("finance.invoices.to")}>
            <input
              id="ivTo"
              type="date"
              value={to()}
              onInput={(e) => setTo(e.currentTarget.value)}
            />
          </Field>
        </div>
        <div class="grid g-2e" style={{ gap: "10px" }}>
          <Field id="ivIssued" label={t("finance.invoices.issuedOn")}>
            <input
              id="ivIssued"
              type="date"
              value={issued()}
              onInput={(e) => setIssued(e.currentTarget.value)}
            />
          </Field>
          <Field id="ivNet" label={t("finance.invoices.net")}>
            <input
              id="ivNet"
              inputmode="decimal"
              value={net()}
              placeholder="zł"
              onInput={(e) => setNet(e.currentTarget.value)}
            />
          </Field>
        </div>
        <Show when={medium() !== "water"}>
          <Field
            id="ivKwh"
            label={t("finance.invoices.kwh")}
            hint={t("finance.invoices.kwhHint")}
          >
            <input
              id="ivKwh"
              inputmode="decimal"
              value={kwh()}
              placeholder="kWh"
              onInput={(e) => setKwh(e.currentTarget.value)}
            />
          </Field>
        </Show>
        <div>
          <div class="eyebrow" style={{ "margin-bottom": "6px" }}>
            {t("finance.invoices.split")}
          </div>
          <div class="grid g-2e" style={{ gap: "10px" }}>
            <For each={CATEGORIES_OF[medium()]}>
              {(cat) => (
                <Field id={`iv-${cat}`} label={t(`finance.category.${cat}`)}>
                  <input
                    id={`iv-${cat}`}
                    inputmode="decimal"
                    value={parts()[cat] ?? ""}
                    placeholder="zł"
                    onInput={(e) =>
                      setParts({ ...parts(), [cat]: e.currentTarget.value })
                    }
                  />
                </Field>
              )}
            </For>
          </div>
          <p class="hint" style={{ margin: "6px 0 0" }}>
            {t("finance.invoices.splitHint")}
          </p>
        </div>
        <p class="hint" style={{ margin: 0, opacity: 0.6 }}>
          {t("finance.invoices.pdfSoon")}
        </p>
        <Show when={error()}>
          <ErrorBox error={error()} />
        </Show>
      </div>
    </Dialog>
  );
}

function AcceptDialog(props: {
  check: InvoiceCheck;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [text, setText] = createSignal("");
  const [run, error, busy] = useAction();
  return (
    <Dialog
      title={t("finance.invoices.acceptTitle", {
        number: props.check.invoice.number,
      })}
      onClose={props.onClose}
      footer={
        <>
          <button type="button" class="btn" onClick={() => props.onClose()}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            class="btn primary"
            disabled={busy() || !text().trim()}
            onClick={() =>
              void run(async () => {
                await api.finance.acceptInvoice(
                  props.check.invoice.id,
                  text().trim(),
                );
                props.onSaved();
                props.onClose();
              })
            }
          >
            {t("finance.invoices.accept")}
          </button>
        </>
      }
    >
      <div style={{ display: "grid", gap: "10px" }}>
        <Field id="acText" label={t("finance.invoices.explanation")}>
          <textarea
            id="acText"
            rows={3}
            maxlength={1000}
            value={text()}
            placeholder={t("finance.invoices.explanationPlaceholder")}
            onInput={(e) => setText(e.currentTarget.value)}
          />
        </Field>
        <Show when={error()}>
          <ErrorBox error={error()} />
        </Show>
      </div>
    </Dialog>
  );
}
